import { takeLatest, put, call } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  IContact,
  GetContactsResponse,
  BulkCreateContactsResponse,
  GetContactSegmentsResponse,
  GetContactActivityResponse,
} from '../../types/contacts/contacts.types';
import { GetContactEmailsResponse } from '../../types/contacts/contacts.types';
import {
  GET_CONTACTS_REQUEST,
  GET_CONTACT_REQUEST,
  CREATE_CONTACT_REQUEST,
  DELETE_CONTACT_REQUEST,
  GetContactsRequestAction,
  GetContactRequestAction,
  CreateContactRequestAction,
  DeleteContactRequestAction,
  UPDATE_CONTACT_REQUEST,
  UpdateContactRequestAction,
  BULK_CREATE_CONTACTS_REQUEST,
  BulkCreateContactsRequestAction,
  BULK_DELETE_CONTACTS_REQUEST,
  BulkDeleteContactsRequestAction,
  EXPORT_CONTACTS_CSV_REQUEST,
  ExportContactsCSVRequestAction,
  DELETE_ALL_CONTACTS_REQUEST,
  DeleteAllContactsRequestAction,
  GET_CONTACT_SEGMENTS_REQUEST,
  GetContactSegmentsRequestAction,
  GET_CONTACT_ACTIVITY_REQUEST,
  GetContactActivityRequestAction,
  GET_CONTACT_EMAILS_REQUEST,
  GetContactEmailsRequestAction,
} from './contacts.types';
import {
  getContactsSuccess,
  getContactsFailure,
  getContactSuccess,
  getContactFailure,
  createContactSuccess,
  createContactFailure,
  deleteContactSuccess,
  deleteContactFailure,
  updateContactSuccess,
  updateContactFailure,
  bulkCreateContactsSuccess,
  bulkCreateContactsFailure,
  bulkDeleteContactsSuccess,
  bulkDeleteContactsFailure,
  exportContactsCSVSuccess,
  exportContactsCSVFailure,
  deleteAllContactsSuccess,
  deleteAllContactsFailure,
  getContactSegmentsSuccess,
  getContactSegmentsFailure,
  getContactActivitySuccess,
  getContactActivityFailure,
  getContactEmailsSuccess,
  getContactEmailsFailure,
} from './contacts.action';
import * as contactsService from '../../services/contactsService/contactsService';
import * as segmentChangeHistoryService from '../../services/segmentChangeHistoryService/segmentChangeHistoryService';
import { getAttributesRequest } from 'store/attributes';

// Worker Sagas
function* getContactsWorker(action: GetContactsRequestAction): SagaIterator {
  try {
    const response: GetContactsResponse = yield call(
      contactsService.getContacts,
      action.payload
    );
    yield put(getContactsSuccess(response));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch contacts';
    yield put(getContactsFailure(errorMessage));
  }
}

function* getContactWorker(action: GetContactRequestAction): SagaIterator {
  try {
    const contact: IContact = yield call(
      contactsService.getContactById,
      action.payload
    );
    yield put(getContactSuccess(contact));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch contact';
    yield put(getContactFailure(errorMessage));
  }
}

function* createContactWorker(
  action: CreateContactRequestAction
): SagaIterator {
  try {
    const contact: IContact = yield call(
      contactsService.createContact,
      action.payload
    );
    yield put(createContactSuccess(contact));
    yield put(getAttributesRequest());
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to save contact';
    yield put(createContactFailure(errorMessage));
  }
}

function* updateContactWorker(
  action: UpdateContactRequestAction
): SagaIterator {
  try {
    const contact: IContact = yield call(contactsService.updateContact, {
      id: action.payload.id,
      data: action.payload.data,
    });
    yield put(updateContactSuccess(contact));
    yield put(getAttributesRequest());
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to update contact';
    yield put(updateContactFailure(errorMessage));
  }
}

function* deleteContactWorker(
  action: DeleteContactRequestAction
): SagaIterator {
  try {
    yield call(contactsService.deleteContact, action.payload);
    yield put(deleteContactSuccess(action.payload));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to delete contact';
    yield put(deleteContactFailure(errorMessage));
  }
}

function* bulkCreateContactsWorker(
  action: BulkCreateContactsRequestAction
): SagaIterator {
  try {
    const response: BulkCreateContactsResponse = yield call(
      contactsService.bulkCreateContacts,
      action.payload
    );
    yield put(bulkCreateContactsSuccess(response));
    yield put(getAttributesRequest());
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to bulk create contacts';
    yield put(bulkCreateContactsFailure(errorMessage));
  }
}

function* bulkDeleteContactsWorker(
  action: BulkDeleteContactsRequestAction
): SagaIterator {
  try {
    yield call(contactsService.bulkDeleteContacts, action.payload);
    yield put(bulkDeleteContactsSuccess(action.payload));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to bulk delete contacts';
    yield put(bulkDeleteContactsFailure(errorMessage));
  }
}

function* exportContactsCSVWorker(
  action: ExportContactsCSVRequestAction
): SagaIterator {
  try {
    const blob: Blob = yield call(
      contactsService.exportContactsCSV,
      action.payload
    );
    yield put(exportContactsCSVSuccess(blob));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to export contacts';
    yield put(exportContactsCSVFailure(errorMessage));
  }
}

function* deleteAllContactsWorker(
  action: DeleteAllContactsRequestAction
): SagaIterator {
  try {
    yield call(contactsService.deleteAllContacts);
    yield put(deleteAllContactsSuccess());
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to delete all contacts';
    yield put(deleteAllContactsFailure(errorMessage));
  }
}

function* getContactSegmentsWorker(
  action: GetContactSegmentsRequestAction
): SagaIterator {
  try {
    const response: GetContactSegmentsResponse = yield call(
      contactsService.getContactSegments,
      action.payload.contactId,
      action.payload.filters
    );
    yield put(getContactSegmentsSuccess(response));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch contact segments';
    yield put(getContactSegmentsFailure(errorMessage));
  }
}

function* getContactActivityWorker(
  action: GetContactActivityRequestAction
): SagaIterator {
  try {
    const response: GetContactActivityResponse = yield call(
      segmentChangeHistoryService.getContactActivity,
      action.payload
    );
    yield put(getContactActivitySuccess(response));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch contact activity';
    yield put(getContactActivityFailure(errorMessage));
  }
}

function* getContactEmailsWorker(
  action: GetContactEmailsRequestAction
): SagaIterator {
  try {
    const response: GetContactEmailsResponse = yield call(
      contactsService.getContactEmails,
      action.payload.contactId,
      action.payload.filters
    );
    yield put(getContactEmailsSuccess(response));
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch contact emails';
    yield put(getContactEmailsFailure(errorMessage));
  }
}

// Root Saga
export function* contactsSaga(): SagaIterator {
  yield takeLatest(GET_CONTACTS_REQUEST, getContactsWorker);
  yield takeLatest(GET_CONTACT_REQUEST, getContactWorker);
  yield takeLatest(CREATE_CONTACT_REQUEST, createContactWorker);
  yield takeLatest(UPDATE_CONTACT_REQUEST, updateContactWorker);
  yield takeLatest(DELETE_CONTACT_REQUEST, deleteContactWorker);
  yield takeLatest(BULK_CREATE_CONTACTS_REQUEST, bulkCreateContactsWorker);
  yield takeLatest(BULK_DELETE_CONTACTS_REQUEST, bulkDeleteContactsWorker);
  yield takeLatest(EXPORT_CONTACTS_CSV_REQUEST, exportContactsCSVWorker);
  yield takeLatest(DELETE_ALL_CONTACTS_REQUEST, deleteAllContactsWorker);
  yield takeLatest(GET_CONTACT_SEGMENTS_REQUEST, getContactSegmentsWorker);
  yield takeLatest(GET_CONTACT_ACTIVITY_REQUEST, getContactActivityWorker);
  yield takeLatest(GET_CONTACT_EMAILS_REQUEST, getContactEmailsWorker);
}
